import React from 'react';
import type { JobAd } from '../../types';
import { SearchAd } from '../search/SearchAd';
import { useSearchAdClasses } from '../search/SearchAdClasses';
import type {
    BaseSearchAdProps,
    SearchAdLayout,
} from '../search/SearchAdTypes';
import { JobHeading } from '../search/job/JobHeading';
import { JobImage } from '../search/job/JobImage';
import { LegendaryAdImageOptions } from './LegendaryAdImageOptions';
import { LegendaryJobDateLocation } from './LegendaryJobDateLocation';
import { SecondaryTag } from './SecondayTag';

const gridClasses = {
    mobile: {
        ad: '',
        imageWrapper: 'mb-8',
        imageAspect:
            'aspect-3/2 grid s-bg rounded-8 border s-border overflow-hidden',
        image: 'm-auto',
        location: 'pr-44 order-first space-x-16 text-xs s-text-subtle',
        content: 'flex flex-col',
        heading: 'mb-8 pr-44 break-words',
        summary:
            'mb-8 flex justify-between space-x-12 whitespace-nowrap font-bold',
        organisation: 'flex flex-col text-xs',
        ribbon: 'absolute top-10 left-10  pointer-events-none',
        favorite: 'relative order-first',
        favoriteButton: 'absolute top-0 right-0',
    },
    desktop: {},
};

const listClasses = {
    mobile: {
        ad: 'grid grid-cols-3 grid-rows-1 gap-x-16',
        imageWrapper: '',
        imageAspect:
            'aspect-3/2 grid s-bg rounded-8 border s-border overflow-hidden',
        image: 'm-auto',
        location:
            'col-span-2 order-first pr-44 space-x-16 text-xs s-text-subtle',
        content: 'col-span-2 flex flex-col',
        heading: 'mb-8 pr-44 break-words',
        summary:
            'mb-8 flex justify-between space-x-12 whitespace-nowrap font-bold',
        organisation: 'flex flex-col text-xs',
        ribbon: 'absolute top-10 left-10 pointer-events-none',
        favorite: '',
        favoriteButton: '',
    },
    desktop: {},
};

type LegendaryJobSearchAdProps = BaseSearchAdProps & {
    ad: JobAd;
    layout: SearchAdLayout;
};

export function LegendaryJobSearchAd(props: LegendaryJobSearchAdProps) {
    const { ad, className, favorite, image, label, layout, ...searchAdProps } =
        props;

    const layoutClasses = layout === 'grid' ? gridClasses : listClasses;
    const classes = useSearchAdClasses({
        mode: 'responsive',
        mobileClasses: layoutClasses.mobile,
        desktopClasses: layoutClasses.desktop,
    });

    const aggregatedLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'job_aggregated');
    const deadlineLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'siste_frist');
    const easyApplyLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'job_easy_apply');
    const promotedLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'fus');

    const isAggregatedAd = Boolean(aggregatedLabel);
    const isPromoted = Boolean(promotedLabel);

    const baseClass = isAggregatedAd ? 'mb-8' : '';

    const legendaryPromotedLabel = label ? (
        <SecondaryTag className="order-first" render={label} />
    ) : (
        <SecondaryTag
            className="order-first"
            label={promotedLabel}
            type="info"
        />
    );

    return (
        <SearchAd
            adId={ad.id}
            className={`${classes.ad} ${baseClass} ${className}`}
            favorite={layout === 'list' ? favorite : undefined}
            legendary
            {...searchAdProps}
        >
            {!isAggregatedAd && (
                <div className={classes.imageWrapper}>
                    <LegendaryAdImageOptions>
                        <JobImage
                            ad={ad}
                            aspectClass={classes.imageAspect}
                            image={image}
                            imageClass={classes.image}
                        />
                    </LegendaryAdImageOptions>
                </div>
            )}
            <div className={classes.content}>
                <JobHeading ad={ad} className={classes.heading} />
                {favorite && layout === 'grid' && (
                    <div className={classes.favorite}>
                        <span className={classes.favoriteButton}>
                            {favorite()}
                        </span>
                    </div>
                )}
                {legendaryPromotedLabel}
                {easyApplyLabel && !isPromoted && (
                    <SecondaryTag
                        className="order-first"
                        label={easyApplyLabel}
                        type="positive"
                    />
                )}
                {deadlineLabel && !isPromoted && (
                    <div className="order-first mb-8">
                        <span className="s-text-negative">
                            {deadlineLabel.text}
                        </span>
                    </div>
                )}
                {aggregatedLabel && !isPromoted && (
                    <SecondaryTag
                        className="order-first"
                        label={aggregatedLabel}
                        type="neutral"
                    />
                )}
                {!isPromoted && (
                    <LegendaryJobDateLocation
                        ad={ad}
                        className={classes.location}
                    />
                )}
                {ad.job_title && ad.heading !== ad.job_title && (
                    <div className={classes.summary}>
                        <span>{ad.job_title}</span>
                    </div>
                )}
                <div className={classes.organisation}>
                    {ad.company_name && <span>{ad.company_name}</span>}
                    {ad.no_of_positions && (
                        <span>
                            {ad.no_of_positions +
                                (ad.no_of_positions === 1
                                    ? ' stilling'
                                    : ' stillinger')}
                        </span>
                    )}
                </div>
            </div>
        </SearchAd>
    );
}
